import { useDispatch, useSelector } from 'react-redux';

import { getLives, setLivesAction, clearLivesAction, setLiveDetailsAction, setOpenLiveConfirmationAction } from 'features/lives/redux/slice';

import { LivesApiResponse, LiveCardProps, LiveStatusEnum } from './types';
import { LiveMeetingAttendanceConfirmationStatusEnum, LiveMeetingAttendanceConfirmationTypeEnum, LiveVideoCard } from 'app/types/cards/LiveVideoCard';
import { IconsTypes } from 'components/icons';
import { ConditionalLinkTargets, ConditionalLinkTypes } from 'components/conditionalLink';
import { VideoProviderEnum } from 'features/contents';
import { createUrl, request } from 'app/utils';
import { useSchool } from 'features/school';
import { AxiosError, AxiosResponse } from 'axios';
import { Card } from 'app/types/cards/Card';

const useLives = () => {
  const dispatch = useDispatch();

  const { lives, liveCardDetails, productClassUuid, openLiveConfirmation } = useSelector(getLives);

  const { apiGatewayUrl } = useSchool();

  const setLives = (lives: LivesApiResponse) => {
    dispatch(setLivesAction(lives));
  };

  const clearLives = () => {
    dispatch(clearLivesAction());
  };

  const setLiveCardDetails = (liveCardDetails: LiveCardProps | null) => {
    dispatch(setLiveDetailsAction(liveCardDetails));
  };

  const clearLiveCardDetails = () => {
    dispatch(setLiveDetailsAction(null));
  };

  const setOpenLiveConfirmation = (open: boolean, liveCardDetails: LiveCardProps | null) => {
    setLiveCardDetails(liveCardDetails);
    dispatch(setOpenLiveConfirmationAction(open));
  };

  const fetchLive = async (liveUuid: string): Promise<Card> => {
    return new Promise(async (resolve, reject) => {
      await request<Card>({
        url: `/api/v1/School/Dashboard/LiveCard/${liveUuid}`,
        baseURL: apiGatewayUrl,
      })
        .then(({ data }) => {
          setLiveCardDetails(sanitizeCardToLiveCard(data?.liveVideo!));
          resolve(data);
        })
        .catch((error) => reject(error));
    });
  };

  const sanitizeCardToLiveCard = (
    liveVideo: LiveVideoCard,
    { journeyProductSlug }: { journeyProductSlug?: string } = {}
  ): Omit<LiveCardProps, 'theme'> | null => {
    if (!liveVideo) return null;

    // let liveStatusName: string;
    let buttonLabel: string;
    // let buttonAriaLabel: string;
    let buttonIcon: IconsTypes = 'play';

    let liveLink = '';
    let liveLinkType: ConditionalLinkTypes;
    let liveLinkTarget: ConditionalLinkTargets = '_self';

    let teacherName = liveVideo.guests || (liveVideo.teacher && liveVideo.teacher.name) || '';
    let liveDate = liveVideo.date;
    let hasConfirmation = false;

    const noProduct = liveVideo.productClassUuid === '00000000-0000-0000-0000-000000000000';
    const isZoom = liveVideo.videoProvider === VideoProviderEnum.Zoom;

    const productSlug = journeyProductSlug ? journeyProductSlug : liveVideo.slug;

    switch (liveVideo.liveStatus) {
      default:
      case LiveStatusEnum.Scheduled:
        // liveStatusName = 'scheduled';
        buttonLabel = 'Em breve';
        // buttonAriaLabel = `Em breve: ${stripTags(liveVideo.name)}`;
        buttonIcon = 'calendarClock';
        liveLinkType = 'nolink';
        break;

      case LiveStatusEnum.InProgress:
        // liveStatusName = 'inProgress';
        buttonLabel = 'Assistir';
        // buttonAriaLabel = `Assistir live ${stripTags(liveVideo.name)}`;
        buttonIcon = 'play';
        liveLinkType = 'external';
        liveLinkTarget = '_blank';
        liveLink = isZoom
          ? liveVideo.externalLiveMeetingInvitationUrl
            ? liveVideo.externalLiveMeetingInvitationUrl
            : createUrl('liveDetailsZoom', [productSlug, liveVideo.productClassUuid, liveVideo.uuid])
          : liveVideo.externalLiveMeetingInvitationUrl
          ? liveVideo.externalLiveMeetingInvitationUrl
          : createUrl(journeyProductSlug ? 'liveDetailsJourney' : noProduct ? 'liveDetailsNoProduct' : 'liveDetails', [
              productSlug,
              liveVideo.productClassUuid,
              liveVideo.uuid,
            ]);
        break;

      case LiveStatusEnum.RecordedNotAvailable:
        // liveStatusName = 'recordedNotAvailable';
        buttonLabel = 'Aguarde';
        // buttonAriaLabel = `Aguarde: ${stripTags(liveVideo.name)}`;
        buttonIcon = 'calendarClock';
        liveLinkType = 'nolink';
        break;

      case LiveStatusEnum.Recorded:
        // liveStatusName = 'recorded';
        buttonLabel = liveVideo.uuid ? 'Assistir gravação' : 'Aula gravada';
        // buttonAriaLabel = liveVideo.uuid ? `Assistir gravação da live ${stripTags(liveVideo.name)}` : `Aula gravada: ${stripTags(liveVideo.name)}`;
        buttonIcon = liveVideo.uuid ? 'play' : 'lock';
        liveLinkType = liveVideo.uuid ? 'internal' : 'nolink';
        liveLink = createUrl(journeyProductSlug ? 'liveDetailsJourney' : noProduct ? 'liveDetailsNoProduct' : 'liveDetails', [
          productSlug,
          liveVideo.productClassUuid,
          liveVideo.uuid,
        ]);
        break;

      case LiveStatusEnum.RecordNotAvailable:
        // liveStatusName = 'RecordNotAvailable';
        buttonLabel = 'Live não foi gravada';
        // buttonAriaLabel = `Live não foi gravada`;
        buttonIcon = 'eyeOff';
        liveLinkType = 'nolink';
        break;

      case LiveStatusEnum.Rescheduled:
        // liveStatusName = 'scheduled';
        buttonLabel = 'Reagendada';
        // buttonAriaLabel = `Live reagendada: ${stripTags(liveVideo.name)}`;
        buttonIcon = 'calendarRemoveOutline';
        liveLinkType = 'nolink';
        liveDate = liveVideo.rescheduledDate ? liveVideo.rescheduledDate : undefined;
        break;
    }

    switch (liveVideo.attendanceConfirmationStatus) {
      case LiveMeetingAttendanceConfirmationStatusEnum.Expired:
        hasConfirmation = true;
        if (liveLinkType !== 'nolink') buttonIcon = 'accountCancel';

        break;

      case LiveMeetingAttendanceConfirmationStatusEnum.Confirmed:
        hasConfirmation = true;
        if (liveLinkType !== 'nolink') buttonIcon = 'accountCheck';
        break;

      case LiveMeetingAttendanceConfirmationStatusEnum.Pending:
        hasConfirmation = true;
        if (liveLinkType !== 'nolink') buttonIcon = 'accountArrowRight';
        break;

      default:
      case LiveMeetingAttendanceConfirmationStatusEnum.NotNeeded:
        break;
    }

    return {
      liveUuid: liveVideo.liveMeetingUuid,
      liveStatus: liveVideo.liveStatus,
      date: liveDate,
      showDate: liveVideo.showDate,
      image: liveVideo.imageUrl || '',
      hat: undefined,
      name: liveVideo.name,
      teacherName: teacherName,
      link: liveLink,
      linkType: liveLinkType,
      linkTarget: liveLinkTarget,
      disabled: liveLinkType === 'nolink',
      buttonLabel: buttonLabel,
      buttonIcon: buttonIcon,
      hasConfirmation: hasConfirmation,
      confirmationStatus: liveVideo.attendanceConfirmationStatus,
      confirmationCodeExpiresAt: liveVideo.attendanceConfirmationCodeExpiresAt,
      confirmationAt: liveVideo.attendanceConfirmationAt,
    };
  };

  const confirmAttendance = async (
    liveUuid: string,
    confirmationCode?: string,
    confirmationType: LiveMeetingAttendanceConfirmationTypeEnum = 0
  ): Promise<{
    apiResultCode: number;
    apiResultMessage: string;
  }> => {
    return new Promise(async (resolve, reject) => {
      await request({
        url: `/api/v1/School/AssessLiveMeetingAttendanceConfirmation/${liveUuid}`,
        method: 'patch',
        baseURL: apiGatewayUrl,
        params: {
          attendanceConfirmationCode: confirmationCode,
          attendanceConfirmationType: confirmationType,
        },
      })
        .then(
          (
            response: AxiosResponse<{
              apiResultCode: number;
              apiResultMessage: string;
            }>
          ) => resolve(response.data)
        )
        .catch(
          (
            error: AxiosError<{
              apiResultCode: number;
              apiResultMessage: string;
            }>
          ) => reject(error?.response?.data)
        );
    });
  };

  return {
    lives,
    setLives,
    clearLives,

    productClassUuid,

    liveCardDetails,
    setLiveCardDetails,
    clearLiveCardDetails,

    setOpenLiveConfirmation,
    openLiveConfirmation,

    sanitizeCardToLiveCard,
    confirmAttendance,
    fetchLive,
  };
};

export default useLives;
